<template>
  <!-- The way into KodeAI, in the top bar: "Ask KodeAI" beside the
       notifications on a computer (components/Navbar), and the "k" alone in a
       page's phone top bar (variant="icon"). It opens and closes the panel
       App.vue keeps (Kode_AI_Widget), and shows only while that is there.
       Registered globally (main.ts), so a page's top bar just names it. -->
  <button
    v-if="ready"
    ref="button"
    type="button"
    class="kai-trigger"
    :class="[`kai-trigger--${variant}`, { 'is-open': open }]"
    aria-haspopup="dialog"
    :aria-expanded="open ? 'true' : 'false'"
    :aria-label="open ? 'Close KodeAI' : 'Ask KodeAI how to do something in Chabad Digital'"
    :title="open ? 'Close KodeAI' : 'Ask KodeAI'"
    @click="toggle"
  >
    <span class="kai-trigger__mark" aria-hidden="true"><kode-ai-logo /></span>
    <span v-if="variant === 'pill'" class="kai-trigger__label">Ask KodeAI</span>
  </button>
</template>
<script>
import kodeAiLogo from '@/components/kode_ai/Kode_AI_Logo.vue';
import { kodeAiPanel, toggleKodeAi } from '@/utils/kode_ai';

export default {
  name: 'KodeAiButton',
  components: {
    kodeAiLogo,
  },
  props: {
    // 'pill': the icon and "Ask KodeAI" (the desktop top bar); 'icon': the icon alone (a phone's top bar).
    variant: {
      type: String,
      default: 'pill',
      validator: (value) => ['pill', 'icon'].includes(value),
    },
  },
  computed: {
    ready() {
      return kodeAiPanel.ready;
    },
    open() {
      return kodeAiPanel.open;
    },
  },
  methods: {
    toggle() {
      toggleKodeAi(this.$refs.button || null);
    },
  },
};
</script>
<style scoped>
/* Sized and shaped as the top bars' own controls: 38px tall, the pill like
   the user pill beside it, the icon like the phone bar's square buttons. */
.kai-trigger {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 38px;
  padding: 0;
  border: 0;
  background: var(--color-bg-canvas, #FFFFFF);
  color: var(--color-text-primary, #1F2330);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

/* Its outline on ::before and its focus ring on ::after: the apps take the
   border, shadow and outline off a focused button (assets/style/index.css). */
.kai-trigger::before,
.kai-trigger::after {
  content: '';
  position: absolute;
  border-radius: inherit;
  pointer-events: none;
}

.kai-trigger::before {
  inset: 0;
  border: 1px solid var(--color-border, #E6E8EE);
  transition: border-color 0.15s ease;
}

.kai-trigger::after {
  inset: -3px;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.45);
  opacity: 0;
  transition: opacity 0.12s ease;
}

.kai-trigger:focus-visible::after {
  opacity: 1;
}

.kai-trigger:hover {
  background: var(--color-bg-soft, #F9FAFC);
}

.kai-trigger:hover::before,
.kai-trigger.is-open::before {
  border-color: var(--color-primary-border, #C7D2FE);
}

.kai-trigger.is-open {
  background: var(--color-primary-soft, #EEF0FF);
  color: var(--color-primary-hover, #4F46E5);
}

/* The icon, in a pale circle (as the support chat shows a mark): the
   user pill's avatar beside it is the same size. */
.kai-trigger__mark {
  box-sizing: border-box;
  display: inline-flex;
  flex: none;
  width: 30px;
  height: 30px;
  padding: 5px;
  border-radius: 50%;
  background: var(--color-primary-soft, #EEF0FF);
  transition: background-color 0.15s ease;
}

.kai-trigger--pill {
  padding: 0 14px 0 4px;
  border-radius: var(--radius-pill, 999px);
}

/* Open, the pill takes the pale tint and the circle turns white. */
.kai-trigger--pill.is-open .kai-trigger__mark {
  background: var(--color-bg-canvas, #FFFFFF);
}

/* A phone's top bar: the icon alone, a square button like the bar's others. */
.kai-trigger--icon {
  width: 38px;
  border-radius: var(--radius-md, 8px);
  background: transparent;
}

.kai-trigger--icon::before,
.kai-trigger--icon:hover::before {
  border-color: transparent;
}

.kai-trigger--icon:hover {
  background: var(--color-bg-muted, #F4F5F8);
}

.kai-trigger--icon.is-open {
  background: var(--color-primary-soft, #EEF0FF);
}

.kai-trigger--icon.is-open::before {
  border-color: var(--color-primary-border, #C7D2FE);
}

.kai-trigger--icon .kai-trigger__mark {
  width: 100%;
  height: 100%;
  padding: 6px;
  border-radius: inherit;
  background: transparent;
}

/* Under the pointer the sparkles catch the light. */
.kai-trigger:hover :deep(.kai-logo__sparkle--big) {
  transform: rotate(90deg) scale(1.12);
}

.kai-trigger:hover :deep(.kai-logo__sparkle--small) {
  transform: scale(1.35);
}

@media (prefers-reduced-motion: reduce) {
  .kai-trigger,
  .kai-trigger::before,
  .kai-trigger::after {
    transition: none;
  }

  .kai-trigger:hover :deep(.kai-logo__sparkle--big),
  .kai-trigger:hover :deep(.kai-logo__sparkle--small) {
    transform: none;
  }
}
</style>
